import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Search, Filter, CheckSquare, Square, Tag, Trash2, Eye, EyeOff, X, LayoutGrid, LayoutList, Image, Radio, ArrowRightLeft, RotateCcw, Archive, RefreshCw } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import CatalogList from './CatalogList'
import CatalogTable from './CatalogTable'
import CatalogDetail from './CatalogDetail'
import CatalogCreate from './CatalogCreate'
import VodBulkTagModal from './VodBulkTagModal'
import BulkImageModal from './BulkImageModal'
import BulkTypeSwitchModal from './BulkTypeSwitchModal'
import './ContentCatalog.css'

export default function ContentCatalog() {
  const { api } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Data state
  const [items, setItems] = useState([])
  const [allTags, setAllTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  
  // Selection state
  const [selected, setSelected] = useState(null)
  const [checked, setChecked] = useState(new Set())
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('') // '', 'active', 'draft'
  const [eventModeFilter, setEventModeFilter] = useState('') // '', 'live', 'ondemand'
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterIds, setTagFilterIds] = useState(null)
  
  // Modal state
  const [showCreate, setShowCreate] = useState(false)
  const [showBulkTag, setShowBulkTag] = useState(false)
  const [showBulkImage, setShowBulkImage] = useState(false)
  const [showBulkTypeSwitch, setShowBulkTypeSwitch] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  
  // View mode
  const [viewMode, setViewMode] = useState('table') // 'cards' or 'table'

  // Deleted items
  const [showDeleted, setShowDeleted] = useState(false)
  const [deletedItems, setDeletedItems] = useState([])
  const [deletedLoading, setDeletedLoading] = useState(false)
  const [restoringId, setRestoringId] = useState(null)

  // Load data
  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      if (eventModeFilter) params.set('event_mode', eventModeFilter)
      if (searchTerm) params.set('search', searchTerm)

      const data = await api(`/vod?${params}`)
      const loadedItems = data.items || []
      setItems(loadedItems)
      
      // Auto-select from URL query param
      const selectId = searchParams.get('select')
      if (selectId && !selected) {
        const targetItem = loadedItems.find(item => item.tbtv_id === selectId)
        if (targetItem) {
          setSelected(targetItem)
          setShowCreate(false)
          setSearchParams({}, { replace: true })
        }
      }

    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api, statusFilter, eventModeFilter, searchTerm, searchParams, selected, setSearchParams])

  const loadTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => {
    loadItems()
    loadTags()
  }, [loadItems, loadTags])

  // Auto-open creation form from URL param (?create=true) — runs once on mount
  useEffect(() => {
    const createParam = searchParams.get('create')
    if (createParam === 'true') {
      setShowCreate(true)
      setSelected(null)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Tag filter
  useEffect(() => {
    if (!activeTag) {
      setTagFilterIds(null)
      return
    }
    
    async function filterByTag() {
      try {
        const data = await api(`/tags/filter?tag=${encodeURIComponent(activeTag)}&type=vod`)
        // The filter returns tbtv_ids for VOD content
        const ids = (data.items || []).map(item => item.tbtv_id)
        setTagFilterIds(ids)
      } catch {
        setTagFilterIds([])
      }
    }
    filterByTag()
  }, [activeTag, api])

  // Handlers
  function handleSelect(item) {
    setSelected(item)
    setShowCreate(false)
  }

  function handleToggle(tbtvId) {
    setChecked(prev => {
      const next = new Set(prev)
      if (next.has(tbtvId)) {
        next.delete(tbtvId)
      } else {
        next.add(tbtvId)
      }
      return next
    })
  }

  function handleToggleAll(displayItems) {
    if (displayItems.every(item => checked.has(item.tbtv_id))) {
      setChecked(new Set())
    } else {
      setChecked(new Set(displayItems.map(item => item.tbtv_id)))
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  async function handleDelete(tbtvId) {
    if (!confirm('Delete this item? This cannot be undone.')) return
    
    try {
      await api(`/vod/${tbtvId}`, { method: 'DELETE' })
      setStatus('✅ Deleted')
      if (selected?.tbtv_id === tbtvId) setSelected(null)
      loadItems()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleBulkStatus(newStatus) {
    if (checked.size === 0) return
    
    const action = newStatus === 'active' ? 'activate' : 'set to draft'
    if (!confirm(`${action} ${checked.size} item(s)?`)) return
    
    setBulkBusy(true)
    try {
      await api('/vod/bulk-status', {
        method: 'POST',
        body: JSON.stringify({ tbtv_ids: Array.from(checked), status: newStatus })
      })
      setStatus(`✅ ${checked.size} item(s) ${newStatus === 'active' ? 'activated' : 'set to draft'}`)
      clearSelection()
      loadItems()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setBulkBusy(false)
  }

  async function handleBulkDelete() {
    if (checked.size === 0) return
    if (!confirm(`Delete ${checked.size} item(s)? This cannot be undone!`)) return
    
    setBulkBusy(true)
    try {
      for (const tbtvId of checked) {
        await api(`/vod/${tbtvId}`, { method: 'DELETE' })
      }
      setStatus(`✅ Deleted ${checked.size} item(s)`)
      clearSelection()
      if (selected && checked.has(selected.tbtv_id)) setSelected(null)
      loadItems()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setBulkBusy(false)
  }

  async function loadDeletedItems() {
    setDeletedLoading(true)
    try {
      const data = await api('/vod/deleted')
      setDeletedItems(data.items || [])
    } catch (e) {
      setStatus(`Error loading deleted items: ${e.message}`)
    }
    setDeletedLoading(false)
  }

  async function handleRestore(logId) {
    setRestoringId(logId)
    try {
      const data = await api(`/vod/deleted/${logId}/restore`, { method: 'POST' })
      setStatus(`✅ Restored "${data.title}" as draft`)
      setDeletedItems(prev => prev.filter(i => i.id !== logId))
      loadItems()
    } catch (e) {
      setStatus(`Error restoring: ${e.message}`)
    }
    setRestoringId(null)
  }

  function handleCreateClick() {
    setSelected(null)
    setShowCreate(true)
  }

  async function handleRefreshWatchCache() {
    try {
      await api('/vod/refresh-watch-cache', { method: 'POST' })
      setStatus('✅ Watch cache refresh pushed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleCreateComplete() {
    setShowCreate(false)
    loadItems()
    loadTags()
  }

  function handleUpdate() {
    loadItems()
    loadTags()
  }

  async function handleDuplicateComplete(newTbtvId) {
    try {
      const newItem = await api(`/vod/${newTbtvId}`)
      setSelected(newItem)
    } catch {}
    loadItems()
    loadTags()
  }

  async function handleToggleSchedule(item) {
    const newVal = item.include_in_schedule ? 0 : 1
    try {
      await api(`/vod/${item.tbtv_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ include_in_schedule: newVal })
      })
      setStatus(`✅ ${item.title} ${newVal ? 'added to' : 'removed from'} live schedule`)
      loadItems()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Apply tag filter client-side
  const displayItems = tagFilterIds !== null
    ? items.filter(item => tagFilterIds.includes(item.tbtv_id))
    : items

  return (
    <div className="content-catalog">
      <div className="catalog-header">
        <div className="catalog-header-left">
          <h1>Content Catalog</h1>
          <span className="catalog-count">{displayItems.length} item{displayItems.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="catalog-header-right">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <LayoutList size={16} />
            </button>
          </div>
          <button className="btn-ghost" onClick={handleRefreshWatchCache} title="Push VOD cache refresh to watch.tenband.tv">
            <RefreshCw size={14} /> Refresh Watch
          </button>
          <button className="btn-ghost" onClick={() => { setShowDeleted(!showDeleted); if (!showDeleted) loadDeletedItems() }}>
            <Archive size={14} /> Deleted
          </button>
          <button className="btn-primary" onClick={handleCreateClick}>
            <Plus size={16} /> Add Content
          </button>
        </div>
      </div>

      {status && (
        <div className={`catalog-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          {status}
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className={`catalog-layout ${viewMode === 'table' ? 'layout-table' : 'layout-cards'}`}>
        {viewMode === 'cards' ? (
          <>
            <div className="catalog-sidebar">
              {/* Filters */}
              <div className="catalog-filters">
                <div className="filter-group">
                  <label><Search size={14} /> Search</label>
                  <input
                    type="text"
                    placeholder="Search titles..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label><Filter size={14} /> Status</label>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label><Radio size={14} /> Mode</label>
                  <select value={eventModeFilter} onChange={e => setEventModeFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="live">Live</option>
                    <option value="ondemand">On-Demand</option>
                    <option value="highlights">Highlights</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label><Tag size={14} /> Tag</label>
                  <select value={activeTag} onChange={e => setActiveTag(e.target.value)}>
                    <option value="">All Tags</option>
                    {allTags.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bulk Actions */}
              {checked.size > 0 && (
                <div className="bulk-actions">
                  <span className="bulk-count">{checked.size} selected</span>
                  <button onClick={() => setShowBulkTag(true)} disabled={bulkBusy}>
                    <Tag size={12} /> Tag
                  </button>
                  <button onClick={() => setShowBulkImage(true)} disabled={bulkBusy}>
                    <Image size={12} /> Image
                  </button>
                  <button onClick={() => setShowBulkTypeSwitch(true)} disabled={bulkBusy}>
                    <ArrowRightLeft size={12} /> Switch Type
                  </button>
                  <button onClick={() => handleBulkStatus('active')} disabled={bulkBusy}>
                    <Eye size={12} /> Activate
                  </button>
                  <button onClick={() => handleBulkStatus('draft')} disabled={bulkBusy}>
                    <EyeOff size={12} /> Draft
                  </button>
                  <button onClick={handleBulkDelete} disabled={bulkBusy} className="danger">
                    <Trash2 size={12} /> Delete
                  </button>
                  <button onClick={clearSelection} className="clear">
                    <X size={12} />
                  </button>
                </div>
              )}

              {/* List */}
              <CatalogList
                items={displayItems}
                loading={loading}
                selected={selected}
                checked={checked}
                onSelect={handleSelect}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
              />
            </div>

            <div className="catalog-main">
              {showCreate ? (
                <CatalogCreate
                  allTags={allTags}
                  onComplete={handleCreateComplete}
                  onCancel={() => setShowCreate(false)}
                  setStatus={setStatus}
                />
              ) : selected ? (
                <CatalogDetail
                  item={selected}
                  allTags={allTags}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicateComplete}
                  setStatus={setStatus}
                />
              ) : (
                <div className="catalog-empty">
                  <p>Select an item to view details or click "Add Content" to create new.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Table mode: detail on top, filters + table on bottom */}
            <div className="catalog-detail-panel">
              {showCreate ? (
                <CatalogCreate
                  allTags={allTags}
                  onComplete={handleCreateComplete}
                  onCancel={() => setShowCreate(false)}
                  setStatus={setStatus}
                />
              ) : selected ? (
                <CatalogDetail
                  item={selected}
                  allTags={allTags}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicateComplete}
                  setStatus={setStatus}
                  layout="split"
                />
              ) : (
                <div className="catalog-empty">
                  <p>Select an item from the table below to view details, or click "Add Content" to create new.</p>
                </div>
              )}
            </div>

            {/* Filters bar */}
            <div className="catalog-filters-bar">
              <div className="filter-group">
                <label><Search size={14} /></label>
                <input
                  type="text"
                  placeholder="Search titles..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label><Filter size={14} /></label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div className="filter-group">
                <label><Radio size={14} /></label>
                <select value={eventModeFilter} onChange={e => setEventModeFilter(e.target.value)}>
                  <option value="">All Modes</option>
                  <option value="live">Live</option>
                  <option value="ondemand">On-Demand</option>
                  <option value="highlights">Highlights</option>
                </select>
              </div>
              <div className="filter-group">
                <label><Tag size={14} /></label>
                <select value={activeTag} onChange={e => setActiveTag(e.target.value)}>
                  <option value="">All Tags</option>
                  {allTags.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {checked.size > 0 && (
                <div className="bulk-actions-inline">
                  <span className="bulk-count">{checked.size} selected</span>
                  <button onClick={() => setShowBulkTag(true)} disabled={bulkBusy}><Tag size={12} /> Tag</button>
                  <button onClick={() => setShowBulkImage(true)} disabled={bulkBusy}><Image size={12} /> Image</button>
                  <button onClick={() => setShowBulkTypeSwitch(true)} disabled={bulkBusy}><ArrowRightLeft size={12} /> Type</button>
                  <button onClick={() => handleBulkStatus('active')} disabled={bulkBusy}><Eye size={12} /> Activate</button>
                  <button onClick={() => handleBulkStatus('draft')} disabled={bulkBusy}><EyeOff size={12} /> Draft</button>
                  <button onClick={handleBulkDelete} disabled={bulkBusy} className="danger"><Trash2 size={12} /> Delete</button>
                  <button onClick={clearSelection} className="clear"><X size={12} /></button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="catalog-table-panel">
              <CatalogTable
                items={displayItems}
                loading={loading}
                selected={selected}
                checked={checked}
                onSelect={handleSelect}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
                onToggleSchedule={handleToggleSchedule}
              />
            </div>
          </>
        )}
      </div>

      {showBulkTag && (
        <VodBulkTagModal
          tbtvIds={Array.from(checked)}
          allTags={allTags}
          onClose={() => setShowBulkTag(false)}
          onComplete={() => {
            setShowBulkTag(false)
            loadItems()
            loadTags()
          }}
          setStatus={setStatus}
        />
      )}

      {showBulkImage && (
        <BulkImageModal
          tbtvIds={Array.from(checked)}
          onClose={() => setShowBulkImage(false)}
          onComplete={() => {
            setShowBulkImage(false)
            clearSelection()
            loadItems()
          }}
          setStatus={setStatus}
        />
      )}

      {showBulkTypeSwitch && (
        <BulkTypeSwitchModal
          items={items.filter(item => checked.has(item.tbtv_id))}
          onClose={() => setShowBulkTypeSwitch(false)}
          onComplete={() => {
            setShowBulkTypeSwitch(false)
            clearSelection()
            loadItems()
          }}
          setStatus={setStatus}
        />
      )}

      {/* Deleted Items Panel */}
      {showDeleted && (
        <div className="deleted-panel">
          <div className="deleted-panel-header">
            <h3><Archive size={16} /> Deleted Items ({deletedItems.length})</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost btn-sm" onClick={loadDeletedItems} disabled={deletedLoading}>
                {deletedLoading ? 'Loading...' : 'Refresh'}
              </button>
              <button className="btn-ghost btn-sm" onClick={() => setShowDeleted(false)}>
                <X size={14} />
              </button>
            </div>
          </div>
          {deletedLoading && !deletedItems.length ? (
            <div className="deleted-empty">Loading deleted items...</div>
          ) : deletedItems.length === 0 ? (
            <div className="deleted-empty">No deleted items found</div>
          ) : (
            <div className="deleted-table-wrap">
              <table className="deleted-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Mode</th>
                    <th>Tags</th>
                    <th>Deleted</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedItems.map(item => (
                    <tr key={item.id}>
                      <td className="deleted-title">
                        <div>{item.title || item.tbtv_id}</div>
                        <div className="deleted-id">{item.tbtv_id}</div>
                      </td>
                      <td><span className="deleted-badge">{item.video_type || '—'}</span></td>
                      <td><span className={`deleted-badge ${item.event_mode === 'live' ? 'live' : 'vod'}`}>{item.event_mode || 'ondemand'}</span></td>
                      <td className="deleted-tags">{item.tags ? item.tags.split(',').slice(0, 3).join(', ') : '—'}</td>
                      <td className="deleted-date">{item.deleted_at ? new Date(item.deleted_at).toLocaleString() : '—'}</td>
                      <td>
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => handleRestore(item.id)}
                          disabled={restoringId === item.id}
                        >
                          <RotateCcw size={12} /> {restoringId === item.id ? 'Restoring...' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
